// External libraries
import * as React from "react";
import { inject, observer } from "mobx-react";
import { QueryRoute } from "bernie-core";
import { withLocalization } from "bernie-l10n";
import { Experiment, isVariantEnabled } from "components/utility/experiment";
import { useLocalization } from "@shared-ui/localization-context";

// UITK
import { UitkLinkButton } from "uitk-react-button";
import { UitkCardAloha, UitkCardAlohaContentSection } from "uitk-react-cards";
import { UitkFigure, UitkImage } from "uitk-react-images";
import { UitkText } from "uitk-react-text";

// Types
import { MapsProps, MapsInjectedProps } from "./typings";
import { ClickAction } from "./MapsEnums";

// Components
import { useFullscreenOpen } from "./hooks";
import { MapContext } from "./MapContext";

const ModuleMap = React.lazy(() => import(/* webpackChunkName: "module-map" */ "./ModuleMap"));

/**
 * Wrapper Map Component. Here we inject the stores and is rendered the call to action button
 * and is rendered the Main Map Component.
 * @param {MapsProps} props
 */
const MapsComponent: React.FC<MapsProps> = (props: MapsProps & MapsInjectedProps) => {
  const { sendUserToSRP, toggleShowFullscreenMap } = useFullscreenOpen(props);
  const { formatText } = useLocalization();
  const [isClient, setIsClient] = React.useState(false);

  React.useEffect(() => {
    setIsClient(true);
  }, []);

  const { flexComponent, context, hotels, staticMap } = props;
  const shouldOpenMapInSRP = flexComponent.model.clickAction === ClickAction.SEND_TO_SRP;
  const mapCTAClickAction = shouldOpenMapInSRP ? sendUserToSRP : toggleShowFullscreenMap;
  const location = props.flexComponent.model.location ?? props.context!.searchContext.location;
  const ctaText = isVariantEnabled(context, Experiment.Blossom_SRP_Lite_V2)
    ? formatText("maps.callToActionButtonViewInAMap")
    : formatText("maps.callToActionButtonShowMap");

  return (
    <div
      className="Maps uitk-cell all-cell-1-1 uitk-cell all-y-padding-three"
      id={flexComponent.id}
      data-fm={flexComponent.fmId}
      data-fm-title-id={flexComponent.fmTitleId}
    >
      <UitkLinkButton
        onClick={mapCTAClickAction}
        disabled={!hotels.allHotelsOnMap.length}
        className="uitk-grid uitk-cell uitk-map-call-to-action"
      >
        <UitkCardAloha>
          <UitkFigure>
            <UitkImage
              alt={formatText("maps.hotel.multipleHotels.a11yLabel", location?.name)}
              src={staticMap.signedUrl}
            />
          </UitkFigure>
          <UitkCardAlohaContentSection>
            <UitkText size={300} className="uitk-type-center uitk-button-old-link">
              {ctaText}
            </UitkText>
          </UitkCardAlohaContentSection>
        </UitkCardAloha>
      </UitkLinkButton>
      <MapContext.Provider
        value={{
          toggleShowFullscreenMap,
          sendUserToSRP,
          shouldOpenMapInSRP,
        }}
      >
        <QueryRoute query={{ pwaDialogMap: `open-${flexComponent.id}` }}>
          {isClient && (
            <React.Suspense fallback={<div />}>
              <ModuleMap {...props} />
            </React.Suspense>
          )}
        </QueryRoute>
      </MapContext.Provider>
    </div>
  );
};

export const Maps = withLocalization<MapsProps, React.FC<MapsProps>>(
  inject("analytics", "routeStore", "hotels", "staticMap", "poi")(observer(MapsComponent))
);

Maps.displayName = "Maps";

export default Maps;
